import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * The ONE place dates, times, durations and numbers are formatted.
 *
 * Per-screen formatting is how a product ends up showing `12/03/2026` on one page and
 * `Mar 12, 2026` on the next. Every display goes through here.
 *
 * Two kinds of value, deliberately kept apart (see DATABASE_DESIGN.md):
 *   - a DAY is a `YYYY-MM-DD` string. It has no time and no zone — a joining date is
 *     the same day everywhere, and parsing it as an instant shifts it by a day for half
 *     the world.
 *   - an INSTANT is ISO 8601 with an offset, rendered in the viewer's local zone.
 */

const INVALID = '—';

/** `2026-03-12` → `12 Mar 2026`. Never parsed as an instant. */
export function formatDay(day: string | null | undefined): string {
  if (!day) return INVALID;
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return INVALID;

  // Constructed as a LOCAL date from its parts, so no zone conversion can occur.
  const value = new Date(year, month - 1, date);
  return isValid(value) ? format(value, 'd MMM yyyy') : INVALID;
}

/** `2026-03-12` → `12 March 2026`, for a page heading rather than a table cell. */
export function formatDayLong(day: string | null | undefined): string {
  if (!day) return INVALID;
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return INVALID;
  const value = new Date(year, month - 1, date);
  return isValid(value) ? format(value, 'd MMMM yyyy') : INVALID;
}

/** An ISO instant → `12 Mar 2026, 09:41` in the viewer's zone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return INVALID;
  const value = parseISO(iso);
  return isValid(value) ? format(value, 'd MMM yyyy, HH:mm') : INVALID;
}

/** ISO instant in a named zone — default India for company-facing timestamps. */
export function formatDateTimeIn(
  iso: string | null | undefined,
  timeZone = 'Asia/Kolkata',
): string {
  if (!iso) return INVALID;
  const value = parseISO(iso);
  if (!isValid(value)) return INVALID;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(value);
}

/** An ISO instant → `3 days ago`. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return INVALID;
  const value = parseISO(iso);
  return isValid(value) ? formatDistanceToNow(value, { addSuffix: true }) : INVALID;
}

/** Minutes → `7h 30m`. Durations are stored as minutes throughout. */
/**
 * The same, from milliseconds.
 *
 * Work-session durations arrive in milliseconds because they are derived from
 * timestamps; attendance arrives in minutes. A single function taking "a duration"
 * would be ambiguous at every call site, and passing milliseconds to the minutes
 * version renders a nine-hour session as "32,400,000m" — which is exactly the kind of
 * mistake a separate name prevents.
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return INVALID;
  return formatDuration(ms / 60_000);
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return INVALID;

  const sign = minutes < 0 ? '-' : '';
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;

  if (hours === 0) return `${sign}${rest}m`;
  if (rest === 0) return `${sign}${hours}h`;
  return `${sign}${hours}h ${rest}m`;
}

/** `1234` → `1,234`. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return INVALID;
  return new Intl.NumberFormat().format(value);
}

/** Today as a day string, in the viewer's zone. */
export function today(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/** Anything missing renders as the same em dash, never as an empty cell. */
export function orDash(value: string | null | undefined): string {
  return value && value.trim() !== '' ? value : INVALID;
}
